import axios, { AxiosError, AxiosRequestConfig } from "axios";
import { tokenManager } from "../tokenManager";

const MAX_RETRIES = 10; // 最大リトライ回数
const RETRY_DELAY = 10000; // リトライ時の待機時間（ミリ秒単位、10秒）

/**
 * Zendesk API への HTTP リクエストを送信する関数
 *
 * 指定されたAPIエンドポイントに対して、認証トークンを付与した HTTP リクエストを送信します。
 * ネットワークエラーやレートリミットに達した場合は、一定時間待機後に最大 `MAX_RETRIES` 回リトライを行います。
 *
 * @param {string} endpoint - API のエンドポイント（`https://` を含む場合はそのまま使用）
 * @param {string} token - 認証トークン（Bearer トークン）
 * @param {"GET" | "POST" | "PUT" | "DELETE"} method - HTTP メソッド
 * @param {string} [brandUrl] - Zendesk のブランド URL（デフォルトは環境変数から取得）
 * @param {any} [data] - リクエストボディ（POST, PUT の場合に使用）
 * @returns {Promise<T>} - API レスポンスデータ
 * @throws {Error} - リクエスト失敗時にエラーメッセージをスロー
 * @see　APIのRate limitについてのZendeskドキュメント https://developer.zendesk.com/api-reference/introduction/rate-limits/
 */
export const zendeskHttpRequest = async <T>(
  endpoint: string,
  token: string,
  method: "GET" | "POST" | "PUT" | "DELETE",
  brandUrl: string = `https://${import.meta.env.VITE_ZENDESK_SUBDOMAIN}.zendesk.com`,
  data?: any,
): Promise<T> => {
  // TokenManager から最新トークンを取得
  const freshToken = tokenManager.getToken() || token;

  // リクエスト URL の構築（フル URL の場合はそのまま使用）
  const url = endpoint.includes("https://")
    ? endpoint
    : `${brandUrl}/api/v2/${endpoint}`;
  const config: AxiosRequestConfig = {
    url,
    method,
    headers: {
      Authorization: `Bearer ${freshToken}`,
    },
  };

  // リクエストボディの設定（POST, PUT の場合）
  if (data) {
    config.data = data;
    // JSON データの場合は Content-Type を設定
    if (typeof data === "string" && config.headers) {
      config.headers["Content-Type"] = "application/json";
    }
  }

  let retries = 0;
  while (retries < MAX_RETRIES) {
    try {
      const result = await axios.request<T>(config);
      return result.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const axiosError = error as AxiosError;
        const status = axiosError.response?.status;
        if (status === 429 || (!axiosError.response && axiosError.request)) {
          const retryAfter = axiosError.response?.headers?.["retry-after"];
          const delay = retryAfter
            ? parseInt(retryAfter) * 1000
            : RETRY_DELAY * Math.min(retries + 1, 3); // 最大30秒のバックオフ
          console.log(
            `レート上限に達しました (${status || "network"}). ${delay}ms 待機後にリトライします... (${retries + 1}/${MAX_RETRIES})`,
          );
          await new Promise((resolve) => setTimeout(resolve, delay));
          retries++;
        } else {
          throw new Error(createErrorMessage(error));
        }
      } else {
        throw new Error(createErrorMessage(error));
      }
    }
  }
  throw new Error("リトライ回数上限");
};

/**
 * エラーメッセージを生成する関数
 *
 * Axios のエラーレスポンスを解析し、適切なエラーメッセージを返します。
 *
 * @param {unknown} error - キャッチしたエラーオブジェクト
 * @returns {string} - ユーザーに分かりやすいエラーメッセージ
 */
export const createErrorMessage = (error: unknown) => {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError;
    console.error("Axios error occurred:", axiosError.message);
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError;
      let errorMessage: string;

      if (axiosError.response) {
        const status = axiosError.response.status;
        switch (status) {
          case 400:
            errorMessage =
              "リクエストが不正です。サーバーがリクエストを理解できませんでした。";
            break;
          case 401:
            errorMessage = "認証エラーです。無効な認証情報が提供されました。";
            break;
          case 403:
            errorMessage =
              "アクセスが禁止されています。サーバーがリクエストを理解しましたが、権限がありません。";
            break;
          case 404:
            errorMessage = "リクエストされたリソースが存在しません。";
            break;
          case 500:
            errorMessage =
              "サーバー内部エラーが発生しました。サーバーがリクエストを処理できませんでした。";
            break;
          case 502:
            errorMessage =
              "不正なゲートウェイです。サーバーが上流サーバーから無効なレスポンスを受け取りました。";
            break;
          case 503:
            errorMessage =
              "サービス利用不可です。サーバーがリクエストを処理する準備ができていません。";
            break;
          case 504:
            errorMessage =
              "ゲートウェイタイムアウトです。サーバーが上流サーバーからのレスポンスを受け取れませんでした。";
            break;
          default:
            errorMessage = `予期しないステータスコード: ${status}`;
        }
      } else if (axiosError.request) {
        errorMessage = "レスポンスが受信されませんでした。";
      } else {
        errorMessage = `リクエストの設定中にエラーが発生しました: ${axiosError.message}`;
      }
      return errorMessage;
    } else {
      console.error("Non-Axios error occurred:", error);
      return "原因不明のエラーが発生しました:" + error;
    }
  }
};
